import React, { createContext, useState, useContext, ReactNode } from 'react';
import {
  UnifiedTheme,
  UnifiedThemeProvider as BackstageUnifiedThemeProvider,
  themes,
  createUnifiedTheme as backstageCreateTheme,
} from '@backstage/theme';
import MuiThemeProvider, {
  ThemeProviderProps as MuiThemeProviderProps,
} from '@mui/material/styles/ThemeProvider';
import {
  createTheme as muiCreateTheme,
  PaletteOptions,
  ThemeOptions,
} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { defaultThemedMuiComponents } from './components';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import localizedFormat from 'dayjs/plugin/localizedFormat';

const getUserLocale = () => navigator.language.split('-')[0] || 'en';

dayjs.extend(utc);
dayjs.extend(localizedFormat);

interface ThemeContextType {
  theme: 'light' | 'dark';
  setTheme: (theme: 'light' | 'dark') => void;
  palette?: PaletteOptions;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'light',
  setTheme: () => {},
  palette: undefined,
});

export interface ThemeProviderProps {
  children: ReactNode;
  options: {
    unifiedTheme?: {
      dark?: UnifiedTheme;
      light?: UnifiedTheme;
    };
    muiTheme?: {
      dark?: MuiThemeProviderProps['theme'];
      light?: MuiThemeProviderProps['theme'];
    };
    activeTheme?: 'light' | 'dark';
  };
}

/**
 * `ThemeProvider` is a wrapper component that provides the theme context to all
 *
 * @remarks
 * - `UnifiedThemeProvider` is used to provide the unified theme to all components for the backstage theme
 * - `MuiThemeProvider` is used to provide the MUI theme to all components for the MUI theme
 *
 * @param children - The children to render within the theme provider
 * @param options - The options to configure the theme provider
 *
 * @returns A React element that provides the theme context to all children
 *
 * @category Theme and Styling
 */
export const ThemeProvider = ({
  children,
  options,
}: ThemeProviderProps): React.ReactElement => {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  React.useEffect(() => {
    loadLocale();
  }, [navigator.language]);

  const loadLocale = () => {
    const userLocale = getUserLocale();

    import(`dayjs/locale/${userLocale}.js`)
      .then(() => {
        dayjs.locale(userLocale);
      })
      .catch(() => {
        console.warn(
          `Locale ${userLocale} not found, falling back to English.`,
        );
        dayjs.locale('en');
      });
  };

  // Resolve the theme object based on light/dark mode and provided options
  const unifiedTheme = React.useMemo(() => {
    const lightTheme = options.unifiedTheme?.light || themes.light;
    const darkTheme = options.unifiedTheme?.dark || themes.dark;
    return (options.activeTheme ?? theme) === 'light' ? lightTheme : darkTheme;
  }, [theme, options.unifiedTheme, options.activeTheme]);

  // Fallback to default MUI theme if no custom MUI theme is provided
  const { muiTheme, palette } = React.useMemo(() => {
    const lightTheme = options.muiTheme?.light || themes.light;
    const darkTheme = options.muiTheme?.dark || themes.dark;
    const muiTheme =
      (options.activeTheme ?? theme) === 'light' ? lightTheme : darkTheme;
    const palette = (muiTheme as ThemeOptions).palette;

    return { muiTheme, palette };
  }, [theme, options.muiTheme, options.activeTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, palette }}>
      <BackstageUnifiedThemeProvider theme={unifiedTheme}>
        <MuiThemeProvider theme={muiTheme}>
          <CssBaseline enableColorScheme />
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={getUserLocale()}
          >
            {children}
          </LocalizationProvider>
        </MuiThemeProvider>
      </BackstageUnifiedThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const createTheme = (
  theme: ThemeOptions,
): ReturnType<typeof muiCreateTheme> => {
  const mergeTheme = {
    ...theme,
    components: { ...defaultThemedMuiComponents, ...theme.components },
  };

  return muiCreateTheme(mergeTheme);
};

export const createUnifiedTheme = (
  theme: any,
): ReturnType<typeof backstageCreateTheme> => {
  const mergeTheme = {
    ...theme,
    components: { ...defaultThemedMuiComponents, ...theme.components },
  };

  return backstageCreateTheme(mergeTheme);
};

export type { UnifiedThemeOptions, UnifiedTheme } from '@backstage/theme';

export {
  createBaseThemeOptions,
  genPageTheme,
  shapes,
  UnifiedThemeProvider,
  palettes,
} from '@backstage/theme';

declare module '@mui/material/styles/createPalette' {
  export interface Palette {
    tertiary: {
      container: string;
      onColorFixed?: string;
      colorFixed?: string;
      onColorFixedDim?: string;
      onColorFixedVar?: string;
    };
    neutral: {
      neutral80: string;
    };
    extended: {
      green: {
        seed: string;
      };
    };
    surface: Surface;
    inverse: Inverse;
    surfaceContainer: SurfaceContainer;
    surfaceHover: {
      [key in keyof Surface]?: string;
    };
    status: Status;
  }
  export interface PaletteOptions {
    surface: Surface;
    surfaceContainer: SurfaceContainer;
    inverse: Inverse;
    surfaceHover: {
      [key in keyof Surface]?: string;
    };
    status: Status;
  }
  interface SimplePaletteColorOptions {
    primary?: string;
    onColor?: string;
    container?: string;
    onColorContainer?: string;
    onColorFixed?: string;
    colorFixed?: string;
    onColorFixedDim?: string;
    onColorFixedVar?: string;
  }
  interface TypeBackground {
    onBackground?: string;
  }
  interface PaletteColor {
    primary?: string;
    onColor?: string;
    onColorContainer?: string;
    onColorFixed?: string;
    colorFixed?: string;
    onColorFixedDim?: string;
    onColorFixedVar?: string;
    container?: string;
    error60?: string;
    error70?: string;
    error80?: string;
    error90?: string;
  }
}

export type Status = {
  ok: string;
  warning: string;
  error: string;
  pending: string;
  running: string;
  aborted: string;
};

export type Surface = {
  surface: string;
  dim: string;
  bright: string;
  onSurface: string;
  onSurfaceVar: string;
  outline: string;
  outlineVariant: string;
};

export type SurfaceContainer = {
  container: string;
  high: string;
  highest: string;
  low: string;
  lowest: string;
};

export type Inverse = {
  surface: string;
  onSurface: string;
  primary: string;
};

declare module '@mui/material/Alert' {
  interface AlertPropsVariantOverrides {
    messageStandard: true;
    messageInfo: true;
    messageWarning: true;
    messageError: true;
  }
}
